import { Utils } from '../../services';
import {
    getFormLayoutWorkflowOutputPathEntries,
    getRepeatableGroupRowPathsUnderPrefix,
} from '../../services/formLayoutGroupUtils';
import { loopBodyBrickIdsForForeach, sortBricks, stableBrickNodeId } from './workflowFlowUtils';

/**
 * @param {unknown[]} workflowBricks
 * @param {Record<string, object>|undefined} formsById
 * @param {object|null|undefined} editorLayout
 * @param {string|null|undefined} currentBrickStableId
 * @returns {{ path: string, kind: 'index'|'row_field', itemKey: string, groupRoot: string }[]}
 */
function foreachIterationPathDetails(workflowBricks, formsById, editorLayout, currentBrickStableId) {
    if (!workflowBricks?.length || !formsById || !currentBrickStableId) {
        return [];
    }
    const cur = String(currentBrickStableId);
    /** @type {{ path: string, kind: 'index'|'row_field', itemKey: string, groupRoot: string }[]} */
    const out = [];
    (workflowBricks || []).forEach((fb) => {
        if (fb?.type !== 'foreach') {
            return;
        }
        const fid = stableBrickNodeId(fb);
        const body = loopBodyBrickIdsForForeach(workflowBricks, editorLayout, fid);
        if (!body.includes(cur)) {
            return;
        }
        const items_path = fb.options?.items_path != null ? String(fb.options.items_path).trim() : '';
        if (!items_path) {
            return;
        }
        const group_root = items_path.split('.')[0].trim();
        if (!group_root) {
            return;
        }
        const item_key_raw = fb.options?.item_key;
        const item_key = (item_key_raw != null ? String(item_key_raw).trim() : '') || 'item';
        const index_key_raw = fb.options?.index_key;
        const index_key = (index_key_raw != null ? String(index_key_raw).trim() : '') || 'index';

        out.push({ path: index_key, kind: 'index', itemKey: item_key, groupRoot: group_root });

        for (const b of sortBricks(workflowBricks)) {
            const bt = b?.type;
            if (bt !== 'form.handle' && bt !== 'form.show') {
                continue;
            }
            const formId = b?.options?.form_id;
            const form = formId ? formsById[String(formId)] : null;
            if (!form) {
                continue;
            }
            const row_paths = getRepeatableGroupRowPathsUnderPrefix(form, group_root, item_key);
            if (row_paths.length > 0) {
                row_paths.forEach((p) =>
                    out.push({ path: p, kind: 'row_field', itemKey: item_key, groupRoot: group_root })
                );
                break;
            }
        }
    });
    return out;
}

/** @param {import('i18next').TFunction} t */
function brickTypeTitle(type, t) {
    const k = `workflows.bricks.${type}.title`;
    const v = t(k);
    return v === k ? String(type) : v;
}

/** @param {{ path: string, category: string, groupName?: string }} entry */
function labelForFormPathEntry(entry, t) {
    const g = entry.groupName ? String(entry.groupName) : '';
    switch (entry.category) {
        case 'root':
            return t('workflows.context_path_hint.form_root_field');
        case 'group_simple':
            return t('workflows.context_path_hint.form_group_field', { group: g });
        case 'repeatable_row':
            return t('workflows.context_path_hint.form_repeatable_row_template', { group: g });
        case 'repeatable_array':
            return t('workflows.context_path_hint.form_repeatable_array', { group: g });
        case 'repeatable_count':
            return t('workflows.context_path_hint.form_repeatable_count', { group: g });
        case 'list_value':
            return t('workflows.context_path_hint.form_list_value');
        case 'list_count':
            return t('workflows.context_path_hint.form_list_count');
        default:
            return '';
    }
}

/**
 * @param {import('i18next').TFunction} t
 * @returns {string}
 */
function currentLanguage(t) {
    return String(t.i18n?.resolvedLanguage || t.i18n?.language || 'fr');
}

/**
 * Grouped context path suggestions (optgroup-friendly).
 *
 * @param {unknown[]} bricks — predecessors / full list depending on caller; paths are still merged from `bricks` for outputs
 * @param {Record<string, object>|undefined} formsById
 * @param {{ workflowBricks?: unknown[], editorLayout?: object|null, currentBrickStableId?: string|null }} [graphCtx]
 * @param {import('i18next').TFunction} t
 * @returns {{ id: string, label: string, options: { path: string, label: string, formatter: string }[] }[]}
 */
export function getWorkflowContextPathOptionGroups(bricks, formsById, graphCtx, t) {
    const seen = new Set();
    /** @type {{ id: string, label: string, options: { path: string, label: string, formatter: string }[] }[]} */
    const groups = [];

    const workflow_bricks = graphCtx?.workflowBricks || bricks || [];
    const fi = graphCtx?.currentBrickStableId
        ? foreachIterationPathDetails(
              workflow_bricks,
              formsById,
              graphCtx.editorLayout,
              graphCtx.currentBrickStableId
          )
        : [];
    if (fi.length > 0) {
        const options = fi.map((row) => {
            const hint =
                row.kind === 'index'
                    ? t('workflows.context_path_hint.foreach_index', {
                          itemKey: row.itemKey,
                          group: row.groupRoot,
                      })
                    : t('workflows.context_path_hint.foreach_row_field', {
                          itemKey: row.itemKey,
                          group: row.groupRoot,
                      });
            return {
                path: row.path,
                label: `${row.path} — ${hint}`,
                formatter: 'text',
            };
        });
        options.forEach((o) => seen.add(o.path));
        groups.push({
            id: 'foreach_iteration',
            label: t('workflows.context_path_group.foreach_iteration'),
            options: options.sort((a, b) => a.path.localeCompare(b.path)),
        });
    }

    if (formsById && typeof formsById === 'object') {
        sortBricks(bricks || []).forEach((brick) => {
            const bt = brick?.type;
            if (bt !== 'form.show' && bt !== 'form.handle') {
                return;
            }
            const formId = brick?.options?.form_id;
            if (!formId) {
                return;
            }
            const form = formsById[String(formId)];
            if (!form) {
                return;
            }
            const lang = currentLanguage(t);
            const form_title = Utils.localized_or_fallback(form?.name, lang, String(formId));
            const entries = getFormLayoutWorkflowOutputPathEntries(form);
            const options = [];
            entries.forEach((e) => {
                if (seen.has(e.path)) {
                    return;
                }
                seen.add(e.path);
                const hint = labelForFormPathEntry(e, t);
                options.push({
                    path: e.path,
                    label: hint ? `${e.path} — ${hint}` : e.path,
                    formatter: 'text',
                });
            });
            if (options.length > 0) {
                groups.push({
                    id: `form_${formId}`,
                    label: t('workflows.context_path_group.form', { name: form_title }),
                    options: options.sort((a, b) => a.path.localeCompare(b.path)),
                });
            }
        });
    }

    const brick_out = [];
    sortBricks(bricks || []).forEach((brick) => {
        const outputs = brick?.outputs || brick?.getOutputs?.() || [];
        const title = brickTypeTitle(brick?.type, t);
        const step = (brick?.position ?? 0) + 1;
        outputs.forEach((output) => {
            if (!output || String(output).startsWith('__')) {
                return;
            }
            const p = String(output);
            if (seen.has(p)) {
                return;
            }
            seen.add(p);
            brick_out.push({
                path: p,
                label: `${p} — ${t('workflows.context_path_hint.brick_output', { brick: title, step })}`,
                formatter: 'text',
            });
        });
    });
    if (brick_out.length > 0) {
        groups.push({
            id: 'brick_outputs',
            label: t('workflows.context_path_group.brick_outputs'),
            options: brick_out.sort((a, b) => a.path.localeCompare(b.path)),
        });
    }

    const has_cart = [...seen].some((path) => path === 'cart' || path.startsWith('cart.'));
    if (has_cart) {
        const cartBuiltins = [
            { path: 'cart_id', formatter: 'text', hint: 'cart_id_root' },
            { path: 'cart.uuid', formatter: 'text', hint: 'cart_uuid' },
            { path: 'cart.id', formatter: 'text', hint: 'cart_numeric_id' },
            { path: 'cart.status', formatter: 'text', hint: 'cart_builtin' },
            { path: 'cart.amount', formatter: 'money', hint: 'cart_builtin' },
        ];
        const cart_opts = cartBuiltins
            .filter((row) => !seen.has(row.path))
            .map((row) => ({
                path: row.path,
                label: `${row.path} — ${t(`workflows.context_path_hint.${row.hint}`)}`,
                formatter: row.formatter,
            }));
        cart_opts.forEach((o) => seen.add(o.path));
        if (cart_opts.length > 0) {
            groups.push({
                id: 'cart',
                label: t('workflows.context_path_group.cart'),
                options: cart_opts,
            });
        }
    }

    return groups;
}

/**
 * Flat list preserving group order then alphabetical path order within each group.
 *
 * @param {{ options: { path: string, label: string, formatter: string }[] }[]} groups
 * @returns {{ path: string, label: string, formatter: string }[]}
 */
export function flattenWorkflowContextPathGroups(groups) {
    const flat = [];
    (groups || []).forEach((g) => {
        (g.options || []).forEach((o) => flat.push(o));
    });
    return flat;
}

/**
 * All paths suggested in groups (for syntax / known-path checks).
 *
 * @param {{ options: { path: string }[] }[]} groups
 * @returns {Set<string>}
 */
export function pathSetFromContextPathGroups(groups) {
    const s = new Set();
    (groups || []).forEach((g) => {
        (g.options || []).forEach((o) => s.add(o.path));
    });
    return s;
}

/**
 * @param {{ options: { path: string, formatter?: string }[] }[]} groups
 * @param {string} path
 * @returns {{ path: string, label: string, formatter: string }|null}
 */
export function findContextPathMetaInGroups(groups, path) {
    const p = String(path ?? '').trim();
    if (!p) {
        return null;
    }
    for (const g of groups || []) {
        const o = (g.options || []).find((opt) => opt.path === p);
        if (o) {
            return o;
        }
    }
    return null;
}

/**
 * @param {import('i18next').TFunction} t
 */
export function mergeOrphanContextPathGroup(groups, path, formatter, t) {
    const p = String(path ?? '').trim();
    if (!p) {
        return groups || [];
    }
    if (pathSetFromContextPathGroups(groups || []).has(p)) {
        return groups || [];
    }
    return [
        ...(groups || []),
        {
            id: 'saved_path',
            label: t('workflows.context_path_group.out_of_list'),
            options: [{ path: p, label: p, formatter: formatter || 'text' }],
        },
    ];
}

/**
 * Context path suggestions for workflows (same rules as runs columns / brick outputs).
 *
 * @param {unknown[]} bricks
 * @param {Record<string, object>|undefined} formsById
 * @param {{ workflowBricks?: unknown[], editorLayout?: object|null, currentBrickStableId?: string|null }} [graphCtx]
 * @param {import('i18next').TFunction} [t] When omitted, labels are raw paths (legacy).
 * @returns {{ path: string, label: string, formatter: string }[]}
 */
export function getWorkflowContextPathOptions(bricks, formsById, graphCtx, t) {
    if (typeof t !== 'function') {
        return getWorkflowContextPathOptionsLegacy(bricks, formsById, graphCtx);
    }
    return flattenWorkflowContextPathGroups(getWorkflowContextPathOptionGroups(bricks, formsById, graphCtx, t));
}

/**
 * @returns {{ path: string, label: string, formatter: string }[]}
 */
function getWorkflowContextPathOptionsLegacy(bricks, formsById, graphCtx) {
    const paths = [];
    (bricks || []).forEach((brick) => {
        const outputs = brick?.outputs || brick?.getOutputs?.() || [];
        outputs.forEach((output) => {
            if (!output || String(output).startsWith('__')) {
                return;
            }
            paths.push(String(output));
        });
    });

    if (formsById && typeof formsById === 'object') {
        (bricks || []).forEach((brick) => {
            const bt = brick?.type;
            if (bt !== 'form.show' && bt !== 'form.handle') {
                return;
            }
            const formId = brick?.options?.form_id;
            if (!formId) {
                return;
            }
            const form = formsById[String(formId)];
            if (!form) {
                return;
            }
            getFormLayoutWorkflowOutputPathEntries(form).forEach((e) => paths.push(e.path));
        });
    }

    if (formsById && graphCtx?.workflowBricks && graphCtx.currentBrickStableId) {
        foreachIterationPathDetails(
            graphCtx.workflowBricks,
            formsById,
            graphCtx.editorLayout,
            graphCtx.currentBrickStableId
        ).forEach((row) => paths.push(row.path));
    }

    const deduplicated_paths = Array.from(new Set(paths)).sort((a, b) => a.localeCompare(b));
    const has_cart_context = deduplicated_paths.some((path) => path === 'cart' || path.startsWith('cart.'));

    const context_options = deduplicated_paths.map((path) => ({
        path,
        label: path,
        formatter: 'text',
    }));

    if (has_cart_context) {
        const cartBuiltins = [
            { path: 'cart_id', label: 'cart_id', formatter: 'text' },
            { path: 'cart.uuid', label: 'cart.uuid', formatter: 'text' },
            { path: 'cart.id', label: 'cart.id', formatter: 'text' },
            { path: 'cart.status', label: 'cart.status', formatter: 'text' },
            { path: 'cart.amount', label: 'cart.amount', formatter: 'money' },
        ];
        cartBuiltins.forEach((option) => {
            if (!context_options.some((existing) => existing.path === option.path)) {
                context_options.push(option);
            }
        });
    }

    return context_options.sort((a, b) => a.label.localeCompare(b.label));
}

export const CUSTOM_CONTEXT_PATH = '__custom__';
